import React, { Fragment } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import { useFeatureToggle } from '@flopflip/react-broadcast';
import { Button } from 'chaoskit/src/components';

import { Data } from '../../components';

export const ArticleCTAWrapper = ({ children, inline, type }) => {
  const classes = cx('articleCTA-wrapper', {
    'is-green u-contrast': type === 'green',
    'is-white': type === 'white',
    'is-inline': inline,
  });

  return (
    <aside className={classes}>
      <div className="container">
        <div className="articleCTA-content">{children}</div>
      </div>
    </aside>
  );
};

ArticleCTAWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  inline: PropTypes.bool,
  type: PropTypes.oneOf(['green', 'white']).isRequired,
};

export const ArticleCTAButtonWrapper = ({ children }) => (
  <div className="articleCTA-content-right">
    <div className="articleCTA-button-wrapper">{children}</div>
  </div>
);

ArticleCTAButtonWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

export const ArticleCTAContentWrapper = ({ children }) => (
  <div className="articleCTA-content-left u-textMedium">{children}</div>
);

ArticleCTAContentWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

const ArticleCTA = ({ cta, type }) => {
  const isBlogCtaFreeDemoEnabled = useFeatureToggle('blogCtaFreeDemo');

  const {
    allArticleCTAs: { edges: allArticleCTAsData },
    defaultCTAs: { childDataYaml: defaultCTAsData },
  } = useStaticQuery(graphql`
    query ArticleCTAPageData {
      allArticleCTAs: allMarkdownRemark(
        filter: {
          # Make sure we're only querying tutorials
          fileAbsolutePath: { regex: "/src/article-ctas/" }
          frontmatter: { title: { ne: "BLUEPRINT" } }
        }
      ) {
        edges {
          node {
            frontmatter {
              title
              variation
              blurb
              button {
                label
                url
                newWindow
              }
            }
          }
        }
      }

      defaultCTAs: file(name: { eq: "article-ctas" }) {
        childDataYaml {
          defaultBlogCTA
          defaultTutorialCTA
        }
      }
    }
  `);

  const getChosenCTA = () => {
    // Attempt to find matching Article CTA from prop if supplied
    if (cta) {
      const matchedCTA = allArticleCTAsData.find(
        ({ node }) => node.frontmatter.title === cta
      );

      if (matchedCTA) {
        return matchedCTA.node.frontmatter;
      }
    } else {
      const defaultType =
        type === 'blog' ? 'defaultBlogCTA' : 'defaultTutorialCTA';

      const matchedCTA = allArticleCTAsData.find(
        ({ node }) => node.frontmatter.title === defaultCTAsData[defaultType]
      );

      if (matchedCTA) {
        return matchedCTA.node.frontmatter;
      }
    }

    // If we get to this point; we didn't find anything. 😑
    return null;
  };

  const chosenCTA = getChosenCTA();

  // If we don't find one after ALL THAT; bail
  if (!chosenCTA) return null;

  const DefaultContent = ({ featureFlag }) => (
    <Fragment>
      <ArticleCTAContentWrapper>
        <Data markdown>{chosenCTA.blurb}</Data>
      </ArticleCTAContentWrapper>
      <ArticleCTAButtonWrapper>
        <Button
          target={chosenCTA.button.newWindow ? '_blank' : null}
          rel={chosenCTA.button.newWindow ? 'noopener noreferrer' : null}
          type="primary"
          url={chosenCTA.button.url}
          data-featureflag={featureFlag || null}
        >
          {chosenCTA.button.label}
        </Button>
      </ArticleCTAButtonWrapper>
    </Fragment>
  );

  DefaultContent.propTypes = {
    featureFlag: PropTypes.string,
  };

  const FreeContent = () =>
    isBlogCtaFreeDemoEnabled ? (
      <Fragment>
        <ArticleCTAContentWrapper>
          <p>
            Gremlin empowers you to proactively root out failure before it
            causes downtime. See how you can harness chaos to build resilient
            systems by requesting a demo of Gremlin.
          </p>
        </ArticleCTAContentWrapper>
        <ArticleCTAButtonWrapper>
          <Button
            type="primary"
            url="/demo/"
            data-featureflag="blogCtaFreeDemo"
          >
            Request a Demo
          </Button>
        </ArticleCTAButtonWrapper>
      </Fragment>
    ) : (
      <DefaultContent featureFlag="blogCtaFreeDemo" />
    );

  return (
    <ArticleCTAWrapper type={chosenCTA.variation}>
      {type === 'blog' && chosenCTA.title === 'Free' ? (
        <FreeContent />
      ) : (
        <DefaultContent />
      )}
    </ArticleCTAWrapper>
  );
};

ArticleCTA.propTypes = {
  cta: PropTypes.string,
  type: PropTypes.string.isRequired,
};

export default ArticleCTA;
